import { Clipper64, ClipType, FillRule, Path64, Paths64, Point64 } from 'clipper2-js';
import { DEFAULT_FLATTEN, entityPaths, type FlattenOptions } from './flatten';
import { Vec2, arcSweep } from './math';
import { cutPointsFor, splitEntityAt } from './slice';
import type { Entity } from './types';

/**
 * Boolean between two sets of geometry.
 *
 * The result is **geometry, not a polygon soup**: every piece that comes back is a slice of an
 * entity that went in — a circle that gets cut becomes arcs of the same radius, a line stays a
 * line, a rectangle stays its four sides. Nothing is flattened into vertices, so a CNC post
 * processor still sees arcs (G2/G3) and an SVG export still writes arcs instead of a thousand
 * chords.
 *
 * - **Union** slices at the crossings and keeps every piece (exactly coincident pieces collapse).
 *   Nothing falls inside anything, so the drawing looks the same — it is cut, not merged.
 * - **Intersect** and **Inverse** are region operations, but only the *decision* is one: a piece is
 *   kept when it lies on the side of the other region the mode asks for. Clipper still answers
 *   "inside or outside" (holes, coincident edges and self-touching loops are its bread and butter),
 *   but the flattened outline it answers about is a question, never the answer.
 *
 * Construction lines are not part of the profile and take no part in any of it.
 */

export type BooleanMode = 'intersect' | 'union' | 'inverse';

export interface BooleanOptions {
  mode?: BooleanMode;
  /** Pieces closer than this count as the same piece (mm). */
  tolerance?: number;
  /** Chord error used only when asking Clipper what is inside what (mm). */
  chordTolerance?: number;
  maxArcSegments?: number;
}

/** Clipper works in integers; anything finer than a micron is noise at drawing scale. */
const SCALE = 1000;
/** A sample point closer than this to a region boundary is *on* it, not in or out (mm). */
const ON_BOUNDARY = 1e-6;

export function booleanEntities(
  a: readonly Entity[],
  b: readonly Entity[],
  options: BooleanOptions = {},
): Entity[] {
  const mode = options.mode ?? 'intersect';
  const tolerance = Math.max(options.tolerance ?? 0.05, 1e-6);
  const flatten: FlattenOptions = {
    chordTolerance: options.chordTolerance ?? DEFAULT_FLATTEN.chordTolerance,
    maxArcSegments: options.maxArcSegments ?? DEFAULT_FLATTEN.maxArcSegments,
  };

  const sides = [playable(a), playable(b)];
  const pool = [...sides[0], ...sides[1]];
  if (pool.length === 0) return [];

  // Slice at the crossings first — in exact maths, so the cuts land on the curves and the pieces
  // keep their own representation.
  const pieces = sides.map((side) =>
    side.flatMap((entity) => splitEntityAt(entity, cutPointsFor(entity, pool))),
  );

  if (mode === 'union') return dedupePieces([...pieces[0]!, ...pieces[1]!], tolerance);

  const regions = sides.map((side) => regionLoops(side, flatten));
  // Intersect keeps what lies inside the other side; Inverse keeps A outside B and B inside A —
  // which together are exactly the boundary of A minus B.
  const wanted = (side: number): Placement =>
    mode === 'intersect' ? 'in' : side === 0 ? 'out' : 'in';

  const kept: Entity[] = [];
  pieces.forEach((list, side) => {
    const other = regions[1 - side]!;
    const want = wanted(side);
    for (const piece of list) if (place(other, sampleOf(piece)) === want) kept.push(piece);
  });
  return kept;
}

/** Construction geometry is a drawing aid: it never takes part in a boolean. */
function playable(entities: readonly Entity[]): Entity[] {
  return entities.filter((entity) => !entity.role);
}

export type Placement = 'in' | 'out' | 'edge';

/**
 * Region loops of one side: its closed shapes unioned together, so two overlapping plates count as
 * one area. Holes come back as their own loops with the opposite orientation, which the winding
 * test below reads correctly.
 */
function regionLoops(entities: readonly Entity[], flatten: FlattenOptions): Vec2[][] {
  const paths = new Paths64();
  for (const entity of entities) {
    for (const path of entityPaths(entity, flatten)) {
      if (!path.closed || path.points.length < 3) continue;
      paths.push(toPath(path.points));
    }
  }
  if (paths.length === 0) return [];

  const engine = new Clipper64();
  engine.addSubjectPaths(paths);
  const closed = new Paths64();
  const open = new Paths64();
  engine.execute(ClipType.Union, FillRule.NonZero, closed, open);
  const loops: Vec2[][] = [];
  for (const loop of closed) {
    const points = fromPath(loop);
    if (points.length >= 3) loops.push(points);
  }
  return loops;
}

/**
 * Where a point sits against a region: inside, outside, or on its boundary. Boundary is its own
 * answer so a shared edge — which is inside neither plate — is never kept by an intersection.
 */
export function place(loops: readonly Vec2[][], point: Vec2): Placement {
  if (loops.length === 0) return 'out';
  let winding = 0;
  let nearest = Infinity;
  for (const loop of loops) {
    for (let index = 0; index < loop.length; index++) {
      const from = loop[index]!;
      const to = loop[(index + 1) % loop.length]!;
      nearest = Math.min(nearest, distanceToSegment(from, to, point));
      const side = (to.x - from.x) * (point.y - from.y) - (point.x - from.x) * (to.y - from.y);
      if (from.y <= point.y) {
        if (to.y > point.y && side > 0) winding += 1;
      } else if (to.y <= point.y && side < 0) {
        winding -= 1;
      }
    }
  }
  if (nearest <= ON_BOUNDARY) return 'edge';
  return winding !== 0 ? 'in' : 'out';
}

/** A point that stands for the piece: on it, and away from its ends when it has any. */
function sampleOf(entity: Entity): Vec2 {
  switch (entity.type) {
    case 'line':
      return Vec2.mid(entity.a, entity.b);
    case 'polyline': {
      if (entity.vertices.length === 0) return Vec2.of(0, 0);
      const index = Math.max(0, entity.vertices.length - 2) >> 1;
      return Vec2.mid(entity.vertices[index]!, entity.vertices[index + 1] ?? entity.vertices[index]!);
    }
    case 'circle':
      return Vec2.polar(entity.radius, 0.7).add(entity.center);
    case 'arc': {
      const sweep = arcSweep(entity.startAngle, entity.endAngle, entity.ccw);
      const angle = entity.startAngle + (entity.ccw ? 1 : -1) * sweep * 0.5;
      return Vec2.polar(entity.radius, angle).add(entity.center);
    }
    default: {
      const path = entityPaths(entity, DEFAULT_FLATTEN)[0];
      if (!path || path.points.length === 0) return Vec2.of(0, 0);
      if (path.points.length === 1) return path.points[0]!;
      return Vec2.mid(path.points[0]!, path.points[path.points.length - 1]!);
    }
  }
}

/** Exactly coincident pieces are one piece: a shared border is drawn once, not twice. */
function dedupePieces(pieces: readonly Entity[], tolerance: number): Entity[] {
  const seen = new Set<string>();
  const out: Entity[] = [];
  for (const piece of pieces) {
    const key = pieceKey(piece, tolerance);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(piece);
  }
  return out;
}

function pieceKey(entity: Entity, tolerance: number): string {
  // Coincident means coincident: a micron is the finest thing a drawing means, and a coarser
  // threshold would merge pieces that are genuinely apart — a 0.05 mm gap is still a gap.
  const grid = Math.min(Math.max(tolerance, 1e-6), 1e-3);
  const round = (value: number): number => Math.round(value / grid);
  const point = (value: Vec2): string => `${round(value.x)},${round(value.y)}`;
  switch (entity.type) {
    case 'line': {
      const ends = [point(entity.a), point(entity.b)].sort();
      return `line|${ends[0]}|${ends[1]}`;
    }
    case 'polyline': {
      // A shared border may be walked from either end; the piece is the same piece.
      const forward = entity.vertices.map(point).join(';');
      const backward = [...entity.vertices].reverse().map(point).join(';');
      return `polyline|${entity.closed ? 'c' : 'o'}|${forward <= backward ? forward : backward}`;
    }
    case 'circle':
      return `circle|${point(entity.center)}|${round(entity.radius)}`;
    case 'arc': {
      // Two halves of one circle share their ends and their sweep — the midpoint is what tells
      // them apart, so an arc is keyed by where its belly is, not by its ends.
      const sweep = arcSweep(entity.startAngle, entity.endAngle, entity.ccw);
      const belly = Vec2.polar(entity.radius, entity.startAngle + (entity.ccw ? 1 : -1) * sweep * 0.5);
      return `arc|${point(entity.center)}|${round(entity.radius)}|${point(belly.add(entity.center))}|${Math.round(sweep * 1e4)}`;
    }
    default: {
      const paths = entityPaths(entity, DEFAULT_FLATTEN);
      return `${entity.type}|${paths.map((path) => path.points.map(point).join(';')).join('|')}`;
    }
  }
}

function toPath(points: readonly Vec2[]): Path64 {
  const path = new Path64();
  for (const point of points) {
    path.push(new Point64(Math.round(point.x * SCALE), Math.round(point.y * SCALE)));
  }
  return path;
}

function fromPath(path: Path64): Vec2[] {
  const out: Vec2[] = [];
  for (const point of path) {
    const next = Vec2.of(point.x / SCALE, point.y / SCALE);
    const last = out[out.length - 1];
    if (last && last.distanceTo(next) < 1e-9) continue;
    out.push(next);
  }
  while (out.length > 2 && out[0]!.distanceTo(out[out.length - 1]!) < 1e-9) out.pop();
  return out;
}

function distanceToSegment(a: Vec2, b: Vec2, point: Vec2): number {
  const ab = b.sub(a);
  const lengthSq = ab.lenSq();
  if (lengthSq < 1e-18) return point.distanceTo(a);
  const t = Math.max(0, Math.min(1, point.sub(a).dot(ab) / lengthSq));
  return point.distanceTo(a.add(ab.scale(t)));
}
